<template>
  <div
    class="manual-merging-section"
    data-testid="manual-merging-section"
  >
    <div class="form-group has-icon-left border-radius-small max-w-30rem mb-20">
      <AppInput
        v-model="search"
        type="search"
        placeholder="Search full name, username, or email"
        data-testid="manual-merging-search-input"
        @input="onSearch"
      />
      <i class="hi hi-search"></i>
      <i
        v-if="search"
        style="left: auto !important; right: 15px"
        class="hi hi-close hi-10 input-clear clickable"
        @click="clearSearch"
      ></i>
    </div>

    <AppScrollableTable
      class="table-bordered table-rounded"
      :has-fixed-column="false"
      data-testid="manual-merging-table"
    >
      <AppTable
        :table-data="pagedRows"
        :loading="loading"
      >
        <template #empty>
          <div
            class="pt-30 table-empty merge-empty-state pb-20 text-center"
            data-testid="manual-empty-state"
          >
            <img
              :src="IMAGE_EMPTY_BOX"
              class="mb-12"
              alt="no silent members"
            />

            <h5>No silent members</h5>
            <p class="text-muted">
              There are no silent members yet. When new<br />
              members are created, they will appear here.
            </p>
          </div>
        </template>
        <AppTableColumn
          header="Member"
          name="member"
        >
          <template #default="{ row }">
            <div
              class="d-flex align-items-center gap-5"
              :data-testid="`manual-merging-row-${row.user.id}`"
            >
              <HubAvatar
                :user-id="row.user.id"
                avatar-style="medium"
                :users-avatars="usersAvatars"
              />
              <div>
                <div>{{ row.user.name }}</div>
                <div class="small text-lighter">
                  {{ row.os_username }}
                  <span
                    v-if="extraOsUsernamesCount(row) > 0"
                    class="text-muted"
                  >
                    (+{{ extraOsUsernamesCount(row) }}
                    {{ extraOsUsernamesCount(row) === 1 ? 'username' : 'usernames' }})
                  </span>
                </div>
                <div class="small text-lighter">{{ row.user.email }}</div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Device"
          name="device"
        >
          <template #default="{ row }">
            <template v-if="row.devices_count === 0"> — </template>
            <template v-else>
              <div v-if="row.device">
                <span
                  v-tooltip
                  :title="row.device.machine_name"
                  class="ellipsis d-inline-block max-w-30rem overflow-hidden"
                >{{ row.device.machine_name }}</span>
                <span
                  v-if="row.devices_count === 1"
                  class="d-block text-lighter"
                >{{ row.device.os_platform }} ({{ row.device.os_version }})</span>
                <span
                  v-else
                  class="d-block text-lighter"
                >+{{ row.devices_count - 1 }} {{ row.devices_count - 1 === 1 ? 'device' : 'devices' }}</span>
              </div>
              <div v-else>—</div>
            </template>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Date added"
          name="added"
        >
          <template #default="{ row }">
            {{ row.added_at }}<br />
            <span class="text-lighter">{{ row.added_time }} {{ row.time_zone_abbr }}</span>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Billing status"
          name="billing_status"
          :header-tooltip="billingGraceTooltip"
        >
          <template #default="{ row }">
            <div class="d-flex">
              <div class="d-flex flex-column align-items-center">
                <span
                  :class="['label', row.billing_status === 'Billed' ? 'label-success-lighter' : 'label-danger-lighter']"
                >
                  {{ row.billing_status }}
                </span>
                <div
                  v-if="row.days_left"
                  class="small text-lighter mt-5"
                >
                  {{ row.days_left }} {{ $filters.pluralize(row.days_left, 'day') }} left
                </div>
              </div>
            </div>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="App version"
          name="appv"
        >
          <template #default="{ row }">
            <template v-if="row.app_version">
              {{ row.app_version }}
              <span
                v-if="extraAppVersionsCount(row) > 0"
                class="d-block text-lighter"
              >+{{ extraAppVersionsCount(row) }}
                {{ extraAppVersionsCount(row) === 1 ? 'version' : 'versions' }}</span>
            </template>
            <template v-else> — </template>
          </template>
        </AppTableColumn>
        <AppTableColumn
          header="Actions"
          name="actions"
        >
          <template #default="{ row }">
            <AppButton
              size="small"
              :data-testid="`manual-merge-action-${row.user.id}`"
              @click="openMergeDialog(row)"
            >
              Merge duplicate
            </AppButton>
          </template>
        </AppTableColumn>
      </AppTable>
    </AppScrollableTable>

    <AppPagination
      v-if="paginationPayload && paginationPayload.total_pages > 1"
      :pagination="paginationPayload"
      :resource="{ one: 'member', many: 'members' }"
      @page-changed="onPageChanged"
    />

    <ClientAgentManualMergeDialog
      v-if="showDialog"
      :duplicate-user="selectedDuplicate"
      @close="closeDialog"
      @confirm-merge="handleMergeConfirm"
    />
  </div>
</template>

<script>
import ClientAgentManualMergeDialog from '~/components/client_agents/ClientAgentManualMergeDialog.vue';
import * as Resources from '~/resources';
import { scrollToTop } from '~/utils/scroll_utils';
import { IMAGE_EMPTY_BOX } from '~/rails_assets';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';
import CorpAppBillingMixin from '~/mixins/CorpAppBillingMixin';

export default {
  name: 'ManualMergingSection',
  components: { ClientAgentManualMergeDialog },
  mixins: [createUsersAvatarsMixin('avatarUsers'), CorpAppBillingMixin],
  props: {
    organizationId: { type: [String, Number], required: true },
    mergingUserIds: { type: Array, default: () => [] },
  },
  emits: ['merge-started'],
  data() {
    return {
      rows: [],
      loading: false,
      search: '',
      page: 1,
      perPage: 20,
      totalCount: 0,
      showDialog: false,
      selectedDuplicate: null,
      merging: false,
      IMAGE_EMPTY_BOX,
      paginationPayload: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage) || 1;
    },
    pagedRows() {
      return this.rows.map((r) => {
        const disabledByServer = r.dup_merge_in_progress || r.main_merge_in_progress;
        const disabledByClient = this.mergingUserIds.includes(r.user.id);

        if (disabledByServer || disabledByClient) {
          return { ...r, trClasses: ['row-merging'] };
        }

        return r;
      });
    },
    avatarUsers() {
      return this.rows.map((r) => ({ id: r.user.id, avatar: r.user.avatar_url }));
    },
  },
  mounted() {
    this.fetchRows();
  },
  methods: {
    async fetchRows() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage, ...(this.search && { search: this.search }) };
        const res = await Resources.OrganizationsResource.mergeMembersSilent({ id: this.organizationId }, params);

        this.rows = (res.members || []).map((m) => ({
          ...m,
          client_agent: { os_username: m.os_username },
        }));
        this.paginationPayload = res.pagination || null;
        this.totalCount = this.paginationPayload ? this.paginationPayload.total_count : this.rows.length;

        const lastPage = this.totalPages;

        if (this.page > lastPage) {
          this.page = lastPage;
          await this.fetchRows();
        }
      } catch (e) {
        console.error(e);
        this.$appMessage('Something went wrong. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchRows();
    },
    clearSearch() {
      this.search = '';
      this.onSearch();
    },
    async onPageChanged(page) {
      this.page = page;
      await this.fetchRows();
      this.$nextTick(() => {
        scrollToTop(this.$el);
      });
    },
    openMergeDialog(row) {
      this.selectedDuplicate = row;
      this.showDialog = true;
    },
    extraAppVersionsCount(row) {
      const uniqueVersions = new Set((row.devices || []).map((d) => d.client_version));

      uniqueVersions.delete(row.app_version);

      return uniqueVersions.size;
    },
    extraOsUsernamesCount(row) {
      const uniqueUsernames = new Set(row.os_usernames);

      uniqueUsernames.delete(row.os_username);

      return uniqueUsernames.size;
    },
    closeDialog() {
      this.showDialog = false;
      this.selectedDuplicate = null;
    },
    async handleMergeConfirm({ fromIds, toId }) {
      if (this.merging) {
        return;
      }
      this.merging = true;
      try {
        await Resources.OrganizationsResource.mergeMembers(
          { id: this.organizationId },
          { from_user_ids: fromIds, to_user_id: toId },
        );
        this.$emit('merge-started', { fromIds, toId });
      } catch (e) {
        console.error(e);
        this.$appMessage('Something went wrong when we tried to merge these members. Please try again later.', {
          header: 'Merge failed',
        });
      } finally {
        this.merging = false;
        this.closeDialog();
      }
    },
  },
};
</script>
